import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Download,
  Monitor,
  ChevronDown,
  Send,
  RefreshCw,
  X,
} from "lucide-react";
import RegionGate from "./RegionGate";
import Brand from "./Brand";
import { AndroidIcon, AppleIcon } from "./PlatformIcons";
import { telegram } from "./links";
import "./App.css";

const base = process.env.PUBLIC_URL || "";
const windowsSetup = `${base}/downloads/files/animix-setup.exe`;
const appStore =
  "https://apps.apple.com/ge/app/animix-%D0%B1%D0%B8%D0%B1%D0%BB%D0%B8%D0%BE%D1%82%D0%B5%D0%BA%D0%B0-%D0%B0%D0%BD%D0%B8%D0%BC%D0%B5/id6754792375";
const channels = [
  {
    href: telegram.main,
    title: "Основа",
    handle: "@animixlib",
    avatar: "animixlib",
  },
  {
    href: telegram.chat,
    title: "Чатик",
    handle: "@animixchatix",
    avatar: "animixchatix",
  },
  {
    href: telegram.apps,
    title: "Приложения",
    handle: "@animixapps",
    avatar: "animixapps",
  },
];

export default function App() {
  const followDialog = useRef(null);
  const showFollowPrompt = () => followDialog.current?.showModal();
  const [apk, setApk] = useState(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    fetch(`${base}/downloads/release.json`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Release unavailable");
        return response.json();
      })
      .then((release) => {
        if (
          release?.available &&
          /^\/downloads\/files\/[a-zA-Z0-9._-]+\.apk$/.test(release.file)
        ) {
          setApk(release);
          setStatus("ready");
        } else {
          setApk(null);
          setStatus("unavailable");
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatus("error");
      });
    return () => controller.abort();
  }, [attempt]);

  return (
    <RegionGate>
      <main className="download-page">
        <div className="page-title">
          <Brand />
          <div>
            <h1>Скачать Animix</h1>
            <p className="page-intro">Выбери версию для своего устройства.</p>
          </div>
        </div>
        <section className="android-card" aria-labelledby="android-title">
          <div className="android-main">
            <div className="platform-heading">
              <span className="platform-icon android-icon">
                <AndroidIcon />
              </span>
              <div>
                <div className="title-line">
                  <h2 id="android-title">Android</h2>
                  <span className="badge">Полная версия</span>
                </div>
                <p>Все возможности Animix · установка из APK</p>
              </div>
            </div>
            <div className="apk-action">
              {status === "ready" ? (
                <a
                  className="button primary"
                  href={`${base}${apk.file}`}
                  download
                  onClick={showFollowPrompt}
                >
                  <Download size={19} /> Скачать APK
                </a>
              ) : status === "error" ? (
                <button
                  className="button secondary"
                  onClick={() => setAttempt((value) => value + 1)}
                >
                  <RefreshCw size={17} /> Повторить загрузку
                </button>
              ) : (
                <button className="button primary" disabled>
                  <Download size={19} />
                  {status === "loading" ? "Загрузка…" : "APK скоро"}
                </button>
              )}
              <span className="file-meta" role="status">
                {status === "ready"
                  ? ["APK", apk.version, apk.size].filter(Boolean).join(" · ")
                  : status === "error"
                    ? "Не удалось получить файл"
                    : status === "unavailable"
                      ? "Файл пока недоступен"
                      : "Проверяем доступность"}
              </span>
            </div>
          </div>
          <p className="platform-description">
            Скачай полную версию напрямую на Android. Открой APK из загрузок и
            установи приложение — Google Play не нужен.
          </p>
          <div className="lite-note">
            <p>
              <strong>Google Play — Animix Lite.</strong> Возможности сокращены
              из-за ограничений политики Google.
            </p>
            <a
              href="https://play.google.com/store/apps/details?id=lol.animix.app"
              target="_blank"
              rel="noreferrer"
            >
              Открыть Lite <ArrowUpRight size={16} />
            </a>
          </div>
        </section>

        <div className="other-platforms">
          <section className="platform-card" aria-labelledby="windows-title">
            <div className="platform-heading">
              <span className="platform-icon">
                <Monitor size={23} />
              </span>
              <div>
                <h2 id="windows-title">Windows</h2>
                <p>Установщик · EXE</p>
              </div>
            </div>
            <p className="platform-description">
              Приложение для компьютера. Скачай EXE, запусти его и следуй
              подсказкам установщика.
            </p>
            <a
              className="button secondary"
              href={windowsSetup}
              download
              onClick={showFollowPrompt}
            >
              <Download size={17} /> Скачать для Windows
            </a>
          </section>
          <section className="platform-card" aria-labelledby="ios-title">
            <div className="platform-heading">
              <span className="platform-icon apple-icon">
                <AppleIcon />
              </span>
              <div>
                <h2 id="ios-title">iPhone и iPad</h2>
                <p>Доступно в App Store</p>
              </div>
            </div>
            <p className="platform-description">
              Установи Animix из App Store. Коллекции аниме, отзывы и обсуждения
              — на iPhone и iPad.
            </p>
            <a
              className="button secondary"
              href={appStore}
              onClick={showFollowPrompt}
              target="_blank"
              rel="noreferrer"
            >
              Открыть в App Store <ArrowUpRight size={17} />
            </a>
          </section>
        </div>

        <h2 className="section-label">Установка и версии</h2>
        <details className="install-help">
          <summary>
            Как установить APK? <ChevronDown size={18} />
          </summary>
          <ol>
            <li>Скачай APK и открой файл из загрузок.</li>
            <li>
              Если Android попросит, разреши установку для браузера или
              файлового менеджера.
            </li>
            <li>
              Нажми «Установить». После установки разрешение можно отключить.
            </li>
          </ol>
        </details>
        <details className="install-help">
          <summary>
            APK или Google Play — что выбрать? <ChevronDown size={18} />
          </summary>
          <p>
            Для всех возможностей Animix на Android скачай APK с этой страницы.
            В Google Play доступен Animix Lite — версия с сокращёнными
            возможностями из-за ограничений политики Google.
          </p>
        </details>
        <details className="install-help">
          <summary>
            Где искать обновления и помощь? <ChevronDown size={18} />
          </summary>
          <p>
            Новые сборки и инструкции публикуем в{" "}
            <a href={telegram.apps} target="_blank" rel="noreferrer">
              @animixapps
            </a>
            . Если что-то не получается, задай вопрос в{" "}
            <a href={telegram.chat} target="_blank" rel="noreferrer">
              @animixchatix
            </a>
            .
          </p>
        </details>

        <h2 className="section-label">Мы в Telegram</h2>
        <nav className="telegram-links" aria-label="Telegram Animix">
          {channels.map(({ href, title, handle, avatar }) => (
            <a key={href} href={href} target="_blank" rel="noreferrer">
              <img
                className="channel-avatar"
                src={`${base}/avatars/${avatar}.jpg`}
                alt=""
                width="44"
                height="44"
              />
              <span>
                <strong>{title}</strong>
                <small>{handle}</small>
              </span>
              <ArrowUpRight className="external-arrow" size={16} />
            </a>
          ))}
        </nav>
      </main>
      <dialog
        ref={followDialog}
        className="follow-dialog"
        aria-labelledby="follow-title"
        aria-describedby="follow-description"
        onClick={(event) => {
          if (event.target === event.currentTarget)
            followDialog.current.close();
        }}
      >
        <div className="follow-content">
          <form method="dialog" className="follow-close-form">
            <button className="follow-close" aria-label="Закрыть" autoFocus>
              <X size={21} />
            </button>
          </form>
          <span className="follow-icon" aria-hidden="true">
            <Send size={25} />
          </span>
          <h2 id="follow-title">Не потеряй Animix</h2>
          <p id="follow-description">
            Подпишись на оба канала и присоединяйся к чату — новости, обновления
            и помощь всегда будут под рукой.
          </p>
          <div className="follow-links">
            {channels.map(({ href, title, handle, avatar }) => (
              <a key={href} href={href} target="_blank" rel="noreferrer">
                <img
                  className="channel-avatar"
                  src={`${base}/avatars/${avatar}.jpg`}
                  alt=""
                  width="44"
                  height="44"
                />
                <span>
                  <strong>{title}</strong>
                  <small>{handle}</small>
                </span>
                <ArrowUpRight size={18} />
              </a>
            ))}
          </div>
          <form method="dialog">
            <button className="button secondary follow-later">Позже</button>
          </form>
        </div>
      </dialog>
    </RegionGate>
  );
}
